/**
 * What the cart needs to describe *when* and *how* a booking happens.
 *
 * Kept apart from `services.ts`, which is the catalogue. These are the terms
 * of a booking rather than the things being booked, and they change for
 * different reasons - a new time slot is an operations decision, a new service
 * is a catalogue one.
 *
 * ---------------------------------------------------------------------------
 * THERE IS NO BACKEND.
 * ---------------------------------------------------------------------------
 * Slots are a fixed list rather than real availability, the coupon is checked
 * against one hard-coded code, and nothing here is reserved. Every one of
 * those is a lookup against a real API later; the shapes are what the screens
 * consume, so they should survive that swap.
 */

/*
 * The cancellation window is owned by `cancellation.ts`, which enforces it.
 * Imported rather than restated so the policy copy below cannot quote a
 * different number from the code that charges the fee.
 *
 * No cycle: `cancellation.ts` imports from `bookings.ts` (the records), not
 * from this file (the terms).
 */
import { freeWindowHours } from './cancellation';
/* Read at call time inside `bookableFrom`, never destructured here - this file
   is imported at launch, long before the config request lands. */
import { appConfig } from '../services/config';

/**
 * How the work is scheduled.
 *
 * `'instant'` is still in the union because the SERVER still has it: existing
 * bookings carry it, `PaymentOrder.mode` is typed by it, and `bookings.ts`
 * reads it when deciding whether a row has a scheduled start to measure a
 * cancellation window against. What is gone is the app ever PRODUCING one -
 * the cart no longer offers Instant (see `MODES` in `CartScreen`), so every
 * new booking names a time.
 *
 * The picker's own list used to live here as `bookingModes`, alongside an
 * `INSTANT_ETA` of "30–60 mins". Both are gone: `CartScreen` builds the two
 * remaining options with the icons it draws them with, and nothing was
 * reading a second copy of the same list.
 */
export type BookingMode = 'instant' | 'schedule' | 'recurring';

/**
 * Bookable times, as a flat list.
 *
 * Generated rather than typed out: the grid is a fixed start, a fixed end and
 * a fixed step, and a hand-written list of thirty labels is thirty chances to
 * fat-finger a "3:30" into the middle of the afternoon. The three constants
 * are the whole schedule, so moving the first visit an hour earlier is one
 * number, not a rewrite.
 *
 * Real availability varies by day and by professional; this is a fixed grid so
 * the picker has something to lay out. Replace with a per-day fetch and keep
 * the same shape - a list of labels the grid renders three across.
 */

/** First bookable visit, as minutes past midnight. 7:00 AM. */
const SLOT_START_MINUTES = 7 * 60;

/** Last bookable visit. 8:00 PM - nothing starts after this. */
const SLOT_END_MINUTES = 20 * 60;

/** The gap between one slot and the next, in minutes. */
const SLOT_STEP_MINUTES = 30;

/** `450` reads as `7:30 AM` - the format `slotStartIso` parses back. */
const labelFor = (minutes: number): string => {
  const hour24 = Math.floor(minutes / 60);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const mins = String(minutes % 60).padStart(2, '0');
  return `${hour12}:${mins} ${hour24 < 12 ? 'AM' : 'PM'}`;
};

export const timeSlots: string[] = Array.from(
  { length: (SLOT_END_MINUTES - SLOT_START_MINUTES) / SLOT_STEP_MINUTES + 1 },
  (_, i) => labelFor(SLOT_START_MINUTES + i * SLOT_STEP_MINUTES),
);

/** `"09:30"` → `570`, or null for anything that is not `HH:mm`. */
const minutesOf = (clock: string): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(clock);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

/**
 * The grid for a city that has opening hours, or the compiled one for a city
 * that has not.
 *
 * ---------------------------------------------------------------------------
 * WHY THE FALLBACK IS THE DEFAULT AND NOT THE EXCEPTION
 * ---------------------------------------------------------------------------
 * `ops.dutyHours.enabled` ships off, so most deployments send no window at
 * all - and "no window" means the server has no opinion, not that the city is
 * shut. Those keep `timeSlots` exactly as before, which is what every existing
 * screen and test already expects.
 *
 * Anything unusable is treated the same way: a start after the end, a step of
 * zero, a time that is not `HH:mm`. A grid built from a bad window is either
 * empty or enormous, and an empty picker is a business that looks closed.
 * Falling back is always the safe direction here, because the compiled grid is
 * the behaviour that shipped.
 *
 * The end is INCLUSIVE - a visit starting exactly at closing is still staffed,
 * because duty ends at the boundary rather than before it. That matches the
 * old grid, whose last slot was its `SLOT_END_MINUTES` exactly.
 */
export const slotsFor = (window?: {
  start: string;
  end: string;
  stepMinutes: number;
}): string[] => {
  if (!window) return timeSlots;

  const start = minutesOf(window.start);
  const end = minutesOf(window.end);
  const step = Math.floor(window.stepMinutes);

  if (start === null || end === null || step <= 0 || end < start) {
    return timeSlots;
  }

  const count = Math.floor((end - start) / step) + 1;
  return Array.from({ length: count }, (_, i) => labelFor(start + i * step));
};

/**
 * How much notice a slot needs, in hours.
 *
 * ---------------------------------------------------------------------------
 * THIS IS AN OPERATIONS NUMBER, NOT A UI ONE
 * ---------------------------------------------------------------------------
 * Somebody has to be found, told, and given time to travel. Two hours is what
 * dispatch currently needs; it is not a property of the picker and it will
 * change - by city, by trade, by how busy a Saturday is.
 *
 * So it lives here as ONE constant rather than as a comparison written inline
 * at each call site. The server serves it now, and this note's own prediction
 * is what `bookableFrom` below does:
 *
 *   appConfig().minNoticeHours ?? MIN_NOTICE_HOURS
 *
 * There and nowhere else - every screen already asks that function rather than
 * doing the arithmetic itself, which is the whole reason it exists. What
 * survives here is the value used before the config lands, matching the
 * server's own default so the two cannot disagree about an unset row.
 *
 * CLIENT-ONLY, and worth knowing: the picker stops offering slots inside the
 * notice, but `POST /bookings` does not refuse one. This is a promise the app
 * keeps, not a rule the server enforces.
 *
 * Note this is a DIFFERENT number from `FREE_WINDOW_HOURS` in
 * `data/cancellation.ts`, which is six. They are unrelated promises - how late
 * you may book versus how late you may cancel free - and collapsing them into
 * one constant because both happen to be "hours before the visit" would tie a
 * refund policy to a dispatch limit.
 */
export const MIN_NOTICE_HOURS = 2;

/**
 * The notice in force right now - served if it arrived, compiled if it did not.
 *
 * ---------------------------------------------------------------------------
 * A FUNCTION, BECAUSE THE COPY HAD TO STOP QUOTING THE CONSTANT
 * ---------------------------------------------------------------------------
 * `bookableFrom` already read the served value, so the GRID obeyed ops the day
 * the setting shipped. The two sentences that describe the grid did not - they
 * interpolated `MIN_NOTICE_HOURS` directly. Set the platform value to one hour
 * and the picker opened slots an hour out while the line underneath it still
 * read "at least 2 hours' notice": the screen arguing with itself, in a way
 * only a customer would notice.
 *
 * So the number is asked for in one place and everything - the arithmetic and
 * the words - reads it from here.
 */
export const noticeHours = (): number =>
  appConfig().minNoticeHours ?? MIN_NOTICE_HOURS;

/**
 * The notice as a phrase, or null when there is nothing to say.
 *
 * Null at zero on purpose. Ops setting the notice to nought means slots open
 * the moment they are reachable, and "at least 0 hours' notice" is not a
 * relaxed rule stated plainly - it is a sentence that reads like a bug. The
 * callers drop the line entirely and say what is actually true instead.
 *
 * Singular at one, because "1 hours' notice" is the tell that a number was
 * pasted into a string nobody re-read.
 */
/**
 * How close to its slot a booking can still be moved - the server's reschedule
 * cutoff, set by ops. Read at call time, like `noticeHours`.
 */
export const rescheduleCutoffHours = (): number => {
  const hours = appConfig().rescheduleCutoffHours;
  return typeof hours === 'number' && Number.isFinite(hours) && hours >= 0
    ? hours
    : 2;
};

/** "2 hours", "1 hour" - the cutoff as the policy copy says it. */
export const rescheduleCutoffPhrase = (): string => {
  const hours = rescheduleCutoffHours();
  return hours === 1 ? '1 hour' : `${hours} hours`;
};

export const noticePhrase = (): string | null => {
  const hours = noticeHours();
  if (hours <= 0) {
    return null;
  }
  return hours === 1 ? "1 hour's notice" : `${hours} hours' notice`;
};

/**
 * The earliest instant a slot may start.
 *
 * Everything that decides whether a time is still offerable goes through
 * here, so the rule is stated once. `now` is passed in rather than read so
 * this stays pure and a test can pin it.
 */
export const bookableFrom = (now: Date = new Date()): Date =>
  new Date(now.getTime() + noticeHours() * 60 * 60 * 1000);

/**
 * Whether this slot is still far enough away to be booked.
 *
 * The fix for two separate bugs, both of which let a customer choose a time
 * that had already gone:
 *
 *   - the grid offered every slot on any selectable day, so at 12:47 you
 *     could book 7:00 AM TODAY - a visit in the past
 *   - nothing enforced dispatch's notice at all, so 12:47 could book 1:00 PM
 *     and nobody could be sent
 *
 * Both are the same question, which is why they get one answer rather than a
 * past-time check in one place and a notice check in another.
 */
export const isSlotBookable = (
  date: Date,
  time: string,
  now: Date = new Date(),
  /**
   * A floor stricter than the booking notice, for a caller that has one.
   *
   * A reschedule does: the server refuses a move onto a slot inside its own
   * cutoff - six hours by default, not two - and answers `earliestNewSlotAt`
   * so a picker can start where it will be accepted. Passing it in rather
   * than teaching this function about reschedules keeps the rule "the latest
   * of the floors that apply", which is what it has always been.
   *
   * Only ever raises. A `notBefore` earlier than the booking notice is
   * ignored, because no caller may offer a slot dispatch cannot staff.
   */
  notBefore?: Date,
): boolean => {
  const start = new Date(slotStartIso(date, time));
  if (Number.isNaN(start.getTime())) {
    /* Unparseable is not "in the past" - an unreadable label is a bug in the
       grid, and hiding every slot would be a worse symptom than showing one. */
    return true;
  }

  const notice = bookableFrom(now).getTime();
  const floor =
    notBefore && notBefore.getTime() > notice ? notBefore.getTime() : notice;

  return start.getTime() >= floor;
};

/**
 * Days after today a slot may fall on - served if it arrived, else three.
 *
 * `booking.advanceBookingDays`, which the server enforces: 3 is today and the
 * next three days, 0 is today only.
 */
export const ADVANCE_BOOKING_DAYS = 3;

export const advanceBookingDays = (): number => {
  const days = Math.floor(
    appConfig().advanceBookingDays ?? ADVANCE_BOOKING_DAYS,
  );
  return Number.isFinite(days) && days >= 0 ? days : ADVANCE_BOOKING_DAYS;
};

/**
 * Whether this date is inside the booking window, counted in calendar days
 * from `now`. Time of day is ignored on both sides.
 */
export const isWithinBookingWindow = (
  date: Date,
  now: Date = new Date(),
): boolean => {
  const last = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  last.setDate(last.getDate() + advanceBookingDays());
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()) <= last;
};

/** "today and the next 3 days", for copy that states the window. */
export const bookingWindowPhrase = (): string => {
  const days = advanceBookingDays();
  if (days === 0) return 'today only';
  return `today and the next ${days === 1 ? 'day' : `${days} days`}`;
};

/**
 * Whether any slot at all is left on this day.
 *
 * What the date strip needs: a day whose last slot has already passed the
 * notice cutoff must be unselectable, or tapping it lands on a grid with
 * every time greyed out and no explanation. `notBefore` passes straight
 * through, so a day sitting entirely inside a reschedule's cutoff greys out
 * as well.
 */
export const hasBookableSlot = (
  date: Date,
  now: Date = new Date(),
  notBefore?: Date,
): boolean =>
  timeSlots.some(time => isSlotBookable(date, time, now, notBefore));

// ------------------------------------------------------------------ recurring

export type Frequency = 'weekly' | 'fortnightly' | 'monthly';

export const frequencies: Array<{
  key: Frequency;
  label: string;
  blurb: string;
}> = [
  { key: 'weekly', label: 'Weekly', blurb: 'Every week on selected day' },
  { key: 'fortnightly', label: 'Every 2 weeks', blurb: 'Once in 2 weeks' },
  { key: 'monthly', label: 'Monthly', blurb: 'Once a month on selected day' },
];

/** Monday first - the working week the service operates on. */
export const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type TimeBand = {
  key: string;
  label: string;
  /** The hours it covers, so the choice is not just a word. */
  hours: string;
};

/*
 * The bands quote real hours, so they have to agree with `timeSlots` above -
 * a "Morning" band advertising 8 AM while the picker offers 7:00 AM is the
 * app contradicting itself on the same booking.
 */
export const timeBands: TimeBand[] = [
  { key: 'morning', label: 'Morning', hours: '7 AM – 12 PM' },
  { key: 'afternoon', label: 'Afternoon', hours: '12 PM – 5 PM' },
  { key: 'evening', label: 'Evening', hours: '5 PM – 8 PM' },
];

// ---------------------------------------------------------------------- money

/*
 * `SERVICE_FEE`, the flat visit fee this cart used to add, is gone - and so
 * are the local `discount`/`taxes`/`total` this file's constants used to feed.
 *
 * ---------------------------------------------------------------------------
 * THE SERVER PRICES THE BASKET NOW
 * ---------------------------------------------------------------------------
 * `CartScreen` used to compute `itemTotal + SERVICE_FEE + taxes - discount +
 * tip` itself, against a fee this app invented and a `coupons` list below that
 * was fiction for the same reason (see the note that used to live on it). The
 * platform's own fee, coupon, subscription and coin discount, and grand total
 * now come from `POST checkout/quote` - see `services/checkout.ts` - and
 * `CartScreen` renders that response's `lines` and `totalAmount` verbatim.
 * Nothing here should ever again feed a `+`/`-`/`*` on a price.
 *
 * `coupons`/`couponFor`/`Coupon` are gone with it - offers are read from
 * `GET discounts/available` via `useDiscounts`, and a typed or picked code is
 * validated by the quote, not matched against a list in this file.
 */

/** Preset tips, in rupees. `null` is the explicit "No tip". */
export const tipOptions: number[] = [20, 50, 100];

// ----------------------------------------------------------------- the policy

/**
 * The two rules, written from the constants that enforce them.
 *
 * ---------------------------------------------------------------------------
 * THEY ARE DIFFERENT NUMBERS, AND THE COPY USED TO HIDE THAT
 * ---------------------------------------------------------------------------
 * This used to read "Cancel or reschedule at no cost up to 6 hours before the
 * visit", which put both actions under one figure. Only half of it was true:
 *
 *   CANCELLING  is governed by `FREE_WINDOW_HOURS` (6) in `cancellation.ts`.
 *               Inside it a fee applies. That is the six.
 *   RESCHEDULING is governed by `MIN_NOTICE_HOURS` (2), here. It is free at
 *               any distance - moving a booking costs nothing, it just needs
 *               enough notice to staff the new slot.
 *
 * So somebody reading "6 hours" on the booking screen and then meeting "2
 * hours" in the slot picker was looking at two real rules described as one,
 * and the app appeared to contradict itself. Neither number was wrong; the
 * sentence was.
 *
 * Built by template rather than typed, so changing either constant rewrites
 * the copy instead of leaving it to be found later.
 *
 * The window is asked for rather than restated for the same reason - a policy
 * string quoting a different number from the code that charges the fee is the
 * kind of thing nobody notices until a customer screenshots both.
 *
 * ---------------------------------------------------------------------------
 * A FUNCTION, NOT AN OBJECT, AND THAT IS THE POINT
 * ---------------------------------------------------------------------------
 * This was a module-level object literal, so both sentences were built the
 * instant the file was imported - at launch, before `GET /config` has
 * answered. Left that way it would have frozen the compiled fallback into the
 * copy and silently ignored whatever ops had set, which is the exact bug being
 * fixed one file over.
 *
 * Called at render instead. Both screens that show it read it inside their own
 * render, so a config landing a moment later is picked up on the next draw
 * with nothing to invalidate.
 */
export const cancellationPolicy = () => ({
  title: 'Cancellation & Refund Policy',
  summary: `Free cancellation until ${freeWindowHours()} hours before service`,
  /*
   * Rescheduling is free, but only until the server's cutoff - the number ops
   * set as `booking.freeRescheduleHours`. It used to be worded from the
   * booking notice instead, a different setting, so the app promised moves
   * the server then refused.
   */
  detail: `Cancel free of charge up to ${freeWindowHours()} hours before the visit. Rescheduling is free up to ${rescheduleCutoffPhrase()} before the visit.`,
});

// ------------------------------------------------------------------- calendar

/** Short weekday names in the order a month grid needs them, Monday first. */
export const calendarDayNames = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
];

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The cells of a month grid, Monday first, padded to whole weeks.
 *
 * `null` is a leading or trailing blank rather than a date from the
 * neighbouring month - greying out a real date invites a tap on something the
 * grid will not accept.
 */
export const monthGrid = (
  year: number,
  month: number,
): Array<number | null> => {
  const first = new Date(year, month, 1);
  // `getDay` is Sunday-first; shift so Monday is 0.
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();

  const cells: Array<number | null> = [];
  for (let i = 0; i < lead; i++) {
    cells.push(null);
  }
  for (let day = 1; day <= days; day++) {
    cells.push(day);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

/** "Tomorrow, 4:00 PM" - how a chosen slot reads back in the cart. */
export const describeSlot = (date: Date, time: string, today: Date): string => {
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (sameDay(date, today)) {
    return `Today, ${time}`;
  }
  if (sameDay(date, tomorrow)) {
    return `Tomorrow, ${time}`;
  }
  return `${date.getDate()} ${monthNames[date.getMonth()].slice(
    0,
    3,
  )}, ${time}`;
};

/**
 * The chosen slot as a server-ready instant.
 *
 * `time` is one of `timeSlots` - "8:00 AM", "1:00 PM" - not an ISO string, so
 * this is the one place that turns the picker's two fields back into a single
 * `Date`. A new time format only has to be taught here, not everywhere a slot
 * is sent to the API.
 */
export const slotStartIso = (date: Date, time: string): string => {
  const match = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i.exec(time.trim());
  const start = new Date(date);
  if (match) {
    const hour12 = Number(match[1]) % 12;
    const isPm = match[3].toUpperCase() === 'PM';
    start.setHours(hour12 + (isPm ? 12 : 0), Number(match[2]), 0, 0);
  }
  return start.toISOString();
};
